import React, { Component } from 'react';
import { Input } from 'reactstrap';
import DocumentTitle from 'react-document-title';
import { connect } from 'react-redux';

import { setNewPassword } from '../../requests/AccountUserRequests.js';
import * as commonFunctions from '../../utils/CommonFunctions.js';
import { showAlert } from '../../actions/Actions.js';
import '../../css/login/Login.css';
// The mismatch styling (ausInvalidInput / ausInvalidInputTip) lives in this
// sheet alongside AccountUserSettings; import it explicitly so the styling does
// not silently break if this view is ever loaded without AccountUserSettings.
import '../../css/AccountUserSettings.css';

// M7-326: landing page for the password-reset LINK. The reset email now carries
// a link to `/reset-password?reset_password_token=...` (we no longer email a
// temporary password), and this page lets the user choose a new one. The token
// from the URL is the authority — the route is public (no prior login), mirrors
// the /password_reset wrapper, and the token is only ever read from the query
// string, never rendered. On submit it PUTs to `users/password`; a bad/expired
// token or a weak/mismatched password comes back 422 and is surfaced inline.
class SetNewPassword extends Component {
    constructor(props) {
        super(props);

        // Read the reset token once, from the URL query string. Prefer the
        // router-supplied location; fall back to window.location so the page
        // still works if mounted outside a <Route> render.
        const search =
            props.location && typeof props.location.search === 'string'
                ? props.location.search
                : window.location.search;
        const resetPasswordToken = new URLSearchParams(search).get('reset_password_token') || '';

        this.state = {
            resetPasswordToken,
            newPassword: '',
            confirmPassword: '',
            saveAttempted: false,
            isSubmitting: false,
        };
    }

    componentDidMount() {
        // Security (M7-326): the token now lives in component state, so scrub it
        // from the address bar to keep it out of browser history and off the
        // Referer header on any subsequent navigation. Guard replaceState for
        // environments (e.g. tests) where it is unavailable.
        if (
            this.state.resetPasswordToken &&
            window.history &&
            typeof window.history.replaceState === 'function'
        ) {
            window.history.replaceState({}, document.title, '/reset-password');
        }
    }

    // Mirrors AccountUserSettings.shouldWarnPasswordMismatch (M7-511): the inline
    // warning only appears once there is something in Confirm to compare against,
    // or once a submit has been attempted — so it does not flag a mismatch from
    // the first character typed into New Password.
    shouldWarnPasswordMismatch = () => {
        const { newPassword, confirmPassword, saveAttempted } = this.state;
        if (newPassword === confirmPassword) {
            return false;
        }
        return confirmPassword !== '' || saveAttempted;
    };

    setNewPassword = () => {
        const { resetPasswordToken, newPassword, confirmPassword } = this.state;

        if (!commonFunctions.isPasswordsValid(newPassword, confirmPassword)) {
            this.setState({ saveAttempted: true });
            this.props.showAlert(
                'Your passwords must be at least 6 characters and match.',
                'warning'
            );
            return;
        }

        this.setState({ isSubmitting: true });

        setNewPassword({
            reset_password_token: resetPasswordToken,
            password: newPassword,
            password_confirmation: confirmPassword,
        })
            .then(res => {
                if (res && res.success) {
                    this.props.showAlert(
                        'Your password has been updated. Please log in.',
                        'positive'
                    );
                    this.props.history.push('/login');
                } else {
                    this.setState({ isSubmitting: false });
                    this.props.showAlert(
                        'We were unable to update your password at this time.',
                        'negative'
                    );
                }
            })
            .catch(error => {
                this.setState({ isSubmitting: false });
                // An invalid/expired token cannot be recovered from this form —
                // its only exit is requesting a new link. When the 422 body names
                // a token problem, clear the token so render() falls through to the
                // invalid-link view (which offers "Request a New Link") instead of
                // stranding the user on a dead form. Any other 422 (e.g. a weak
                // password) keeps the form and its input, surfacing the message
                // inline. A network/unknown failure falls back to a generic message.
                if (
                    error &&
                    error.response &&
                    error.response.status === 422 &&
                    error.message &&
                    /invalid|expired|token/i.test(error.message)
                ) {
                    this.setState({ resetPasswordToken: '' });
                    return;
                }
                const message =
                    error && error.message
                        ? error.message
                        : 'We were unable to update your password at this time.';
                this.props.showAlert(message, 'negative');
            });
    };

    renderInvalidLink() {
        return (
            <div className="lContainer">
                <DocumentTitle title="Reset Password" />
                <div className="lSignInText">Reset Your Password</div>
                <div className="lInputContainer cardContainer">
                    <div className="lPasswordResetInstruction">
                        This reset link is invalid or has expired — request a new one.
                    </div>
                    <div className="lButtonContainer">
                        <button
                            className="primaryButton"
                            onClick={() => this.props.history.push('/password_reset')}
                        >
                            Request a New Link
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        if (!this.state.resetPasswordToken) {
            return this.renderInvalidLink();
        }

        const mismatchWarning = this.shouldWarnPasswordMismatch();

        return (
            <div className="lContainer">
                <DocumentTitle title="Reset Password" />
                <div className="lSignInText">Choose a New Password</div>
                <div className="lInputContainer cardContainer">
                    <div className="lPasswordResetInstruction">
                        Enter a new password for your account below.
                    </div>
                    <form
                        onSubmit={event => {
                            event.preventDefault();
                            this.setNewPassword();
                        }}
                    >
                        <div className="lLabelAndInput">
                            <div className="lLabel">New Password</div>
                            <Input
                                type="password"
                                autoComplete="new-password"
                                aria-label="New Password"
                                value={this.state.newPassword}
                                onChange={event =>
                                    this.setState({ newPassword: event.target.value })
                                }
                            />
                        </div>
                        <div className="lLabelAndInput">
                            <div className="lLabel">Confirm Password</div>
                            <Input
                                className={mismatchWarning ? 'ausInvalidInput' : ''}
                                type="password"
                                autoComplete="new-password"
                                aria-label="Confirm Password"
                                aria-invalid={mismatchWarning}
                                aria-describedby={
                                    mismatchWarning ? 'setNewPasswordMismatchTip' : undefined
                                }
                                value={this.state.confirmPassword}
                                onChange={event =>
                                    this.setState({ confirmPassword: event.target.value })
                                }
                            />
                        </div>
                        {mismatchWarning && (
                            <div
                                id="setNewPasswordMismatchTip"
                                role="alert"
                                className="ausInvalidInputTip"
                            >
                                Passwords Do Not Match
                            </div>
                        )}
                        <div className="lForgotPasswordRow">
                            <button
                                type="button"
                                className="lForgotPasswordLink"
                                onClick={() => this.props.history.push('/login')}
                            >
                                Login
                            </button>
                        </div>
                        <div className="lButtonContainer">
                            <button
                                type="submit"
                                className="primaryButton"
                                disabled={this.state.isSubmitting}
                            >
                                Set Password
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {};
}

export default connect(
    mapStateToProps,
    { showAlert }
)(SetNewPassword);
